import { Link } from 'react-router-dom'
import LegalLayout from '@/components/LegalLayout'
import Seo from '@/components/Seo'

export default function PrivacyPage() {
  const onPrint = () => window.print()

  return (
    <LegalLayout
      title="Privacy Policy"
      tagline="Your privacy and data security are our priorities"
      effectiveDate="January 24, 2026"
    >
      <Seo
        title="Privacy Policy | Pelvia"
        description="How Pelvia collects, uses, and protects your personal and health data."
        path="/privacy"
      />
      <p>
        At Pelvia, we take your privacy seriously. Please read this Privacy Policy to learn how we
        treat your personal data. This Privacy Policy applies to pelvia.me. By using or accessing
        our Services in any manner, you acknowledge that you accept the practices and policies
        outlined below, and you hereby consent that we will collect, use and disclose your
        information as described in this Privacy Policy.
      </p>

      <p>
        Remember that your use of Pelvia's Services is at all times subject to our{' '}
        <Link to="/terms">Terms of Use</Link>, which incorporates this Privacy Policy. Any terms we
        use in this Policy without defining them have the definitions given to them in the Terms of
        Use.
      </p>

      <p>
        You may print a copy of this Privacy Policy by{' '}
        <button
          type="button"
          onClick={onPrint}
          className="cursor-pointer border-b border-brown/25 bg-transparent p-0 text-brown transition-colors hover:border-brown"
        >
          clicking here
        </button>
        .
      </p>

      <p>
        As we continually work to improve our Services, we may need to change this Privacy Policy
        from time to time. We will alert you of material changes by placing a notice on the Pelvia
        website, by sending you an email and/or by some other means. Please note that if you've
        opted not to receive legal notice emails from us (or you haven't provided us with your
        email address), those legal notices will still govern your use of the Services, and you
        are still responsible for reading and understanding them. If you use the Services after
        any changes to the Privacy Policy have been posted, that means you agree to all of the
        changes.
      </p>

      <p>
        For individuals in the United States, your rights under applicable state consumer privacy
        laws are described in the relevant sections below.
      </p>

      <div className="toc">
        <h3>Privacy Policy Table of Contents</h3>
        <ul>
          <li>What this Privacy Policy Covers</li>
          <li>
            Personal Data
            <ul>
              <li>Categories of Personal Data We Collect</li>
              <li>Our Commercial or Business Purposes for Collecting Personal Data</li>
              <li>Other Permitted Purposes for Processing Personal Data</li>
              <li>Categories of Sources of Personal Data</li>
            </ul>
          </li>
          <li>How We Disclose Your Personal Data</li>
          <li>Tracking Tools, Advertising and Opt-Out</li>
          <li>Data Security</li>
          <li>Personal Data of Children</li>
          <li>State Law Privacy Rights</li>
          <li>Contact Information</li>
        </ul>
      </div>

      <h2>What this Privacy Policy Covers</h2>
      <p>
        This Privacy Policy covers how we treat Personal Data that we gather when you access or
        use our Services. "Personal Data" means any information that identifies or relates to a
        particular individual and also includes information referred to as "personally
        identifiable information" or "personal information" or "sensitive personal information"
        under applicable data privacy laws, rules or regulations. This Privacy Policy does not
        cover the practices of companies we don't own or control or people we don't manage.
      </p>

      <h2>Personal Data</h2>

      <h3>Categories of Personal Data We Collect</h3>
      <p>
        This chart details the categories of Personal Data that we collect and have collected over
        the past 12 months:
      </p>

      <div className="overflow-x-auto">
        <table>
          <thead>
            <tr>
              <th>Category of Personal Data (and Examples)</th>
              <th>Business or Commercial Purpose(s) for Collection</th>
              <th>Categories of Third Parties With Whom We Disclose this Personal Data</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <strong>Profile or Contact Data</strong> such as first and last name, email, phone
                number, and zip code.
              </td>
              <td>
                • Providing, Customizing and Improving the Services
                <br />• Marketing the Services
                <br />• Corresponding with You
              </td>
              <td>
                • Business Partners
                <br />• Parties You Authorize, Access or Authenticate
              </td>
            </tr>
            <tr>
              <td>
                <strong>Device/IP Data</strong> such as IP address, device ID, and type of
                device/operating system/browser used to access the Services.
              </td>
              <td>• Providing, Customizing and Improving the Services</td>
              <td>• Service Providers</td>
            </tr>
            <tr>
              <td>
                <strong>Web Analytics</strong> such as web page interactions.
              </td>
              <td>• Providing, Customizing and Improving the Services</td>
              <td>• Analytics Partners</td>
            </tr>
            <tr>
              <td>
                <strong>Consumer Demographic Data</strong> such as age and/or date of birth, zip
                code, gender, race, and ethnicity.
              </td>
              <td>
                • Providing, Customizing and Improving the Services
                <br />• Marketing the Services
              </td>
              <td>
                • Analytics Partners
                <br />• Business Partners
              </td>
            </tr>
            <tr>
              <td>
                <strong>Professional or Employment-Related Data</strong> such as company name and
                job title.
              </td>
              <td>• Providing, Customizing and Improving the Services</td>
              <td>• Business Partners</td>
            </tr>
            <tr>
              <td>
                <strong>Health Data</strong> such as medical conditions, weight, health or exercise
                activity monitoring, or any information concerning a consumer's health, including
                information about a mental or physical health condition or diagnosis.
              </td>
              <td>
                • Providing, Customizing and Improving the Services
                <br />• Marketing the Services
              </td>
              <td>
                • Service Providers
                <br />• Parties You Authorize, Access or Authenticate
              </td>
            </tr>
            <tr>
              <td>
                <strong>Categories of Data Considered "Sensitive" Under the State Privacy Laws</strong>{' '}
                (as defined below) such as:
                <br />• Race or ethnic origin
                <br />• Health Data
              </td>
              <td>
                • Providing, Customizing and Improving the Services
                <br />• Corresponding with You
              </td>
              <td>
                • Service Providers
                <br />• Parties You Authorize, Access or Authenticate
              </td>
            </tr>
            <tr>
              <td>
                <strong>Inferences Drawn From Other Personal Data Collected</strong> such as user
                attributes.
              </td>
              <td>
                • Providing, Customizing and Improving the Services
                <br />• Marketing the Services
              </td>
              <td>
                • Analytics Partners
                <br />• Business Partners
              </td>
            </tr>
            <tr>
              <td>
                <strong>Other Identifying Information that You Voluntarily Choose to Provide</strong>{' '}
                such as emails, letters, texts, or other communications you send us.
              </td>
              <td>
                • Providing, Customizing and Improving the Services
                <br />• Corresponding with You
              </td>
              <td>
                • Service Providers
                <br />• Parties You Authorize, Access or Authenticate
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3>Our Commercial or Business Purposes for Collecting Personal Data</h3>
      <ul>
        <li>
          <strong>Providing, Customizing and Improving the Services</strong>
          <ul>
            <li>Creating and managing your account or other user profiles.</li>
            <li>Providing you with the products, services or information you request.</li>
            <li>Meeting or fulfilling the reason you provided the information to us.</li>
            <li>Providing support and assistance for the Services.</li>
            <li>
              Improving the Services, including testing, research, internal analytics and product
              development.
            </li>
            <li>
              Personalizing the Services, website content and communications based on your
              preferences.
            </li>
            <li>Doing fraud protection, security and debugging.</li>
          </ul>
        </li>
        <li>
          <strong>Marketing the Services</strong>
          <ul>
            <li>Marketing and selling the Services.</li>
          </ul>
        </li>
        <li>
          <strong>Corresponding with You</strong>
          <ul>
            <li>
              Responding to correspondence that we receive from you, contacting you when necessary
              or requested, and sending you information about Pelvia or the Services.
            </li>
            <li>Sending emails and other communications according to your preferences.</li>
          </ul>
        </li>
      </ul>

      <h3>Other Permitted Purposes for Processing Personal Data</h3>
      <p>
        In addition, each of the above referenced categories of Personal Data may be collected,
        used, and disclosed with the government, including law enforcement, or other parties to
        meet certain legal requirements and enforcing legal terms including: fulfilling our legal
        obligations under applicable law, regulation, court order or other legal process, such as
        preventing, detecting and investigating security incidents and potentially illegal or
        prohibited activities; protecting the rights, property or safety of you, Pelvia or another
        party; enforcing any agreements with you; responding to claims that any posting or other
        content violates third-party rights; and resolving disputes.
      </p>

      <p>
        We will not collect additional categories of Personal Data or use the Personal Data we
        collected for materially different, unrelated or incompatible purposes without providing
        you notice or obtaining your consent.
      </p>

      <h3>Categories of Sources of Personal Data</h3>
      <p>We collect Personal Data about you from the following categories of sources:</p>
      <ul>
        <li>
          <strong>You</strong>
          <ul>
            <li>
              When you provide such information directly to us.
              <ul>
                <li>When you create an account or use our interactive tools and Services.</li>
                <li>
                  When you voluntarily provide information in free-form text boxes through the
                  Services or through responses to surveys or questionnaires.
                </li>
                <li>When you send us an email or otherwise contact us.</li>
              </ul>
            </li>
            <li>
              When you use the Services and such information is collected automatically.
              <ul>
                <li>
                  Through Cookies (defined in the "Tracking Tools, Advertising and Opt-Out" section
                  below).
                </li>
                <li>
                  If you download our mobile application or use a location-enabled browser, we may
                  receive information about your location and mobile device, as applicable.
                </li>
                <li>
                  If you download and install certain applications and software we make available,
                  we may receive and collect information transmitted from your computing device for
                  the purpose of providing you the relevant Services, such as information regarding
                  when you are logged on and available to receive updates or alert notices.
                </li>
              </ul>
            </li>
          </ul>
        </li>
        <li>
          <strong>Third Parties</strong>
          <ul>
            <li>
              Vendors
              <ul>
                <li>
                  We may use analytics providers to analyze how you interact and engage with the
                  Services, or third parties may help us provide you with customer support.
                </li>
              </ul>
            </li>
            <li>
              Third Parties and Business Partners
              <ul>
                <li>
                  We may receive information about you from third-party authentication providers
                  (e.g., Google OAuth).
                </li>
              </ul>
            </li>
          </ul>
        </li>
      </ul>

      <h2>How We Disclose Your Personal Data</h2>
      <p>
        We disclose your Personal Data to the categories of service providers and other parties
        listed in this section. Depending on state laws that may be applicable to you, some of
        these disclosures may constitute a "sale" of your Personal Data. For more information,
        please refer to the state-specific sections below.
      </p>

      <ul>
        <li>
          <strong>Service Providers.</strong> These parties help us provide the Services or perform
          business functions on our behalf. They include:
          <ul>
            <li>Hosting, technology and communication providers.</li>
            <li>Analytics providers for web traffic or usage of the site.</li>
            <li>Security and fraud prevention consultants.</li>
            <li>Support and customer service vendors.</li>
            <li>
              Payment processors.
              <ul>
                <li>
                  Our payment processing partner Stripe, Inc. ("Stripe") collects your
                  voluntarily-provided payment card information necessary to process your payment.
                </li>
                <li>
                  Please see Stripe's terms of service and privacy policy for information on its
                  use and storage of your Personal Data.
                </li>
              </ul>
            </li>
          </ul>
        </li>
        <li>
          <strong>Analytics Partners.</strong> These parties provide analytics on web traffic or
          usage of the Services. They include:
          <ul>
            <li>Companies that track how users found or were referred to the Services.</li>
            <li>Companies that track how users interact with the Services.</li>
          </ul>
        </li>
        <li>
          <strong>Business Partners.</strong> These parties partner with us in offering various
          services. They include:
          <ul>
            <li>
              Businesses that you have a relationship with, such as health insurance providers,
              health systems and health providers who purchase access to Pelvia.
            </li>
            <li>
              Companies that we partner with to offer joint promotional offers or opportunities.
            </li>
          </ul>
        </li>
        <li>
          <strong>Parties You Authorize, Access or Authenticate</strong>
          <ul>
            <li>Third parties you access through the services.</li>
          </ul>
        </li>
      </ul>

      <h3>Legal Obligations</h3>
      <p>
        We may disclose any Personal Data that we collect with third parties in conjunction with
        any of the activities set forth under "Other Permitted Purposes for Processing Personal
        Data" section above.
      </p>

      <h3>Business Transfers</h3>
      <p>
        All of your Personal Data that we collect may be transferred to a third party if we
        undergo a merger, acquisition, bankruptcy or other transaction in which that third party
        assumes control of our business (in whole or in part).
      </p>

      <h3>Data that is Not Personal Data</h3>
      <p>
        We may create aggregated, de-identified or anonymized data from the Personal Data we
        collect, including by removing information that makes the data personally identifiable to
        a particular user. We may use such aggregated, de-identified or anonymized data and
        disclose it with third parties for our lawful business purposes, including to analyze,
        build and improve the Services and promote our business, provided that we will not
        disclose such data in a manner that could identify you.
      </p>

      <h2>Tracking Tools, Advertising, and Opt-Out</h2>
      <p>
        The Services use cookies and similar technologies such as pixel tags, web beacons, clear
        GIFs and JavaScript (collectively, "Cookies") to enable our servers to recognize your web
        browser, tell us how and when you visit and use our Services, analyze trends, learn about
        our user base and operate and improve our Services. Cookies are small pieces of data
        (usually text files) placed on your computer, tablet, phone or similar device when you use
        that device to access our Services. We may also supplement the information we collect from
        you with information received from third parties, including third parties that have placed
        their own Cookies on your device(s).
      </p>

      <p>
        Please note that because of our use of Cookies, the Services do not support "Do Not Track"
        requests sent from a browser at this time.
      </p>

      <p>We use the following types of Cookies:</p>
      <ul>
        <li>
          <strong>Essential Cookies.</strong> Essential Cookies are required for providing you with
          features or services that you have requested. For example, certain Cookies enable you to
          log into secure areas of our Services. Disabling these Cookies may make certain features
          and services unavailable.
        </li>
        <li>
          <strong>Functional Cookies.</strong> Functional Cookies are used to record your choices
          and settings regarding our Services, maintain your preferences over time and recognize
          you when you return to our Services. These Cookies help us to personalize our content
          for you, greet you by name and remember your preferences (for example, your choice of
          language or region).
        </li>
        <li>
          <strong>Performance/Analytical Cookies.</strong> Performance/Analytical Cookies allow us
          to understand how visitors use our Services. They do this by collecting information
          about the number of visitors to the Services, what pages visitors view on our Services
          and how long visitors are viewing pages on the Services. Performance/Analytical Cookies
          also help us measure the performance of our advertising campaigns in order to help us
          improve our campaigns and the Services' content for those who engage with our
          advertising. For example, Google LLC ("Google") uses cookies in connection with its
          Google Analytics services. Google's ability to use and disclose information collected by
          Google Analytics about your visits to the Services is subject to the Google Analytics
          Terms of Use and the Google Privacy Policy. You have the option to opt-out of Google's
          use of Cookies by visiting the Google advertising opt-out page at
          www.google.com/privacy_ads.html or the Google Analytics Opt-out Browser Add-on at
          https://tools.google.com/dlpage/gaoptout/.
        </li>
      </ul>

      <p>
        You can decide whether or not to accept Cookies through your internet browser's settings.
        Most browsers have an option for turning off the Cookie feature, which will prevent your
        browser from accepting new Cookies, as well as (depending on the sophistication of your
        browser software) allow you to decide on acceptance of each new Cookie in a variety of
        ways. You can also delete all Cookies that are already on your device. If you do this,
        however, you may have to manually adjust some preferences every time you visit our website
        and some of the Services and functionalities may not work.
      </p>

      <p>
        To find out more information about Cookies generally, including information about how to
        manage and delete Cookies, please visit{' '}
        <a href="http://www.allaboutcookies.org/" target="_blank" rel="noopener noreferrer">
          http://www.allaboutcookies.org/
        </a>
        .
      </p>

      <h2>Data Security</h2>
      <p>
        We seek to protect your Personal Data from unauthorized access, use and disclosure using
        appropriate physical, technical, organizational and administrative security measures based
        on the type of Personal Data and how we are processing that data. You should also help
        protect your data by appropriately selecting and protecting your password and/or other
        sign-on mechanism; limiting access to your computer or device and browser; and signing off
        after you have finished accessing your account. Although we work to protect the security
        of your account and other data that we hold in our records, please be aware that no method
        of transmitting data over the internet or storing data is completely secure.
      </p>

      <h3>Data Retention</h3>
      <p>
        We retain Personal Data about you for as long as necessary to provide you with our
        Services or to perform our business or commercial purposes for collecting your Personal
        Data. When establishing a retention period for specific categories of data, we consider
        who we collected the data from, our need for the Personal Data, why we collected the
        Personal Data, and the sensitivity of the Personal Data. In some cases we retain Personal
        Data for longer, if doing so is necessary to comply with our legal obligations, resolve
        disputes or collect fees owed, or is otherwise permitted or required by applicable law,
        rule or regulation. We may further retain information in an anonymous or aggregated form
        where that information would not identify you personally.
      </p>

      <p>For example:</p>
      <ul>
        <li>
          We retain your profile information and credentials for as long as you have an account
          with us.
        </li>
        <li>
          We retain your device/IP data for as long as we need it to ensure that our systems are
          working appropriately, effectively and efficiently.
        </li>
      </ul>

      <h2>Personal Data of Children</h2>
      <p>
        As noted in the Terms of Use, we do not knowingly collect or solicit Personal Data from
        children under 16 years of age; if you are a child under the age of 16, please do not
        attempt to register for or otherwise use the Services or send us any Personal Data. If we
        learn we have collected Personal Data from a child under 16 years of age, we will delete
        that information as quickly as possible. If you believe that a child under 16 years of age
        may have provided Personal Data to us, please contact us at support@pelvia.me.
      </p>

      <h2>State Law Privacy Rights</h2>

      <h3>California Resident Rights</h3>
      <p>
        Under California Civil Code Sections 1798.83-1798.84, California residents are entitled to
        contact us to prevent disclosure of Personal Data to third parties for such third parties'
        direct marketing purposes; in order to submit such a request, please contact us at
        support@pelvia.me.
      </p>

      <p>
        Your browser may offer you a "Do Not Track" option, which allows you to signal to
        operators of websites and web applications and services that you do not wish such
        operators to track certain of your online activities over time and across different
        websites. Our Services do not support Do Not Track requests at this time. To find out more
        about "Do Not Track," you can visit www.allaboutdnt.com.
      </p>

      <h3>Nevada Resident Rights</h3>
      <p>
        If you are a resident of Nevada, you have the right to opt-out of the sale of certain
        Personal Data to third parties. Please note that we do not currently sell your Personal
        Data as sales are defined in Nevada Revised Statutes Chapter 603A.
      </p>

      <h3>Transfers of Personal Data</h3>
      <p>
        The Services are hosted and operated in the United States ("U.S.") through Pelvia and its
        service providers, and if you do not reside in the U.S., laws in the U.S. may differ from
        the laws where you reside. By using the Services, you acknowledge that any Personal Data
        about you, regardless of whether provided by you or obtained from a third party, is being
        provided to Pelvia in the U.S. and will be hosted on U.S. servers, and you authorize
        Pelvia to transfer, store and process your information to and in the U.S., and possibly
        other countries. In some circumstances, your Personal Data may be transferred to the U.S.
        pursuant to a data processing agreement incorporating standard data protection clauses.
      </p>

      <h2>Contact Information</h2>
      <p>
        If you have any questions or comments about this Privacy Policy, the ways in which we
        collect and use your Personal Data or your choices and rights regarding such collection
        and use, please do not hesitate to contact us at:{' '}
        <a href="mailto:support@pelvia.me">support@pelvia.me</a>
      </p>
    </LegalLayout>
  )
}
